/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      details: ["+91 8369033791"],
      action: "tel:+918369033791",
    },
    {
      icon: Mail,
      title: "Email",
      details: ["info@dnotours.com"],
      action: "mailto:info@dnotours.com",
    },
    {
      icon: MapPin,
      title: "Office Address",
      details: ["Office 02, Sairaj Gardens,", "Iraniwadi Rd No. 3, Kandivali West,", "Mumbai, Maharashtra 400067"],
    },
    {
      icon: MapPin,
      title: "Registered Office",
      details: ["804, A Wing, Veena Classic,", "Modi Park, Irani Wadi Road No. 3,", "Kandivali West, Mumbai 400067"],
    },
    {
      icon: Clock,
      title: "Working Hours",
      details: ["10:00 AM - 8:00 PM", "Mon - Sat"],
    },
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-900" data-testid="contact-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920&q=80"
            alt="Contact Us"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="caption text-coral mb-4 block">Get In Touch</span>
            <h1 className="heading-1 text-white mb-6">Contact Us</h1>
            <p className="text-lg text-white/80">
              Have questions about your next trip? Our travel experts are here to help. 
              Reach out to us and let's plan your perfect getaway together.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white" data-testid="contact-info">
        <div className="container-custom">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 -mt-32 relative z-10">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full text-center hover:shadow-lg transition-shadow" data-testid={`contact-info-${index}`}>
                  <CardContent className="p-6">
                    <div className="w-14 h-14 mx-auto bg-coral/10 rounded-full flex items-center justify-center mb-4">
                      <info.icon className="h-6 w-6 text-coral" />
                    </div>
                    <h3 className="font-heading font-semibold text-primary mb-3">{info.title}</h3>
                    {info.details.map((detail, idx) => (
                      <p key={detail} className="text-slate-600 text-sm">
                        {info.action && idx === 0 ? (
                          <a href={info.action} className="hover:text-coral transition-colors">
                            {detail}
                          </a>
                        ) : (
                          detail
                        )}
                      </p>
                    ))}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="section-padding bg-accent" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div {...fadeInUp}>
              <Card className="shadow-xl" data-testid="contact-form-card">
                <CardContent className="p-8">
                  <h2 className="heading-3 text-primary mb-2">Send Us a Message</h2>
                  <p className="text-slate-600 mb-6">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="h-12"
                          placeholder="Your name"
                          data-testid="contact-input-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="h-12"
                          placeholder="your@email.com"
                          data-testid="contact-input-email"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="h-12"
                          placeholder="+91 98765 43210"
                          data-testid="contact-input-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="h-12"
                          placeholder="How can we help?"
                          data-testid="contact-input-subject"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Tell us more about your inquiry..."
                        data-testid="contact-textarea-message"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="btn-coral w-full"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                      <Send className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <h2 className="heading-3 text-primary mb-4">Visit Our Office</h2>
                <p className="body-lg mb-6">
                  Located in Kandivali West, Mumbai, our office is easily accessible. 
                  Walk in for a personal consultation with our travel experts.
                </p>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h4 className="font-heading font-semibold text-primary mb-2">
                    DNO Events & DMC Private Limited
                  </h4>
                  <p className="text-slate-600 mb-4">
                    Office 02, Sairaj Gardens,<br />
                    Iraniwadi Rd Number 3,<br />
                    Kandivali West, Mumbai,<br />
                    Maharashtra 400067
                  </p>
                  <h4 className="font-heading font-semibold text-primary mb-2 mt-4">
                    Registered Office
                  </h4>
                  <p className="text-slate-600 mb-4">
                    804, A Wing, Veena Classic,<br />
                    Modi Park, Irani Wadi Road Number 3,<br />
                    Kandivali West, Mumbai,<br />
                    Maharashtra, India 400067
                  </p>
                  <div className="aspect-video rounded-lg overflow-hidden bg-slate-200">
                    <iframe
                      title="Office Location"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3768.0889076927645!2d72.8366!3d19.2094!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7b6f5bc8e9a1b%3A0x9c9c8c9c8c9c8c9c!2sSairaj%20Gardens%2C%20Iraniwadi%20Rd%2C%20Kandivali%20West%2C%20Mumbai%2C%20Maharashtra%20400067!5e0!3m2!1sen!2sin!4v1650000000000!5m2!1sen!2sin"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-primary mb-1">
                      Chat on WhatsApp
                    </h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Get instant responses to your queries via WhatsApp
                    </p>
                    <a
                      href="https://wa.me/918369033791"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="bg-green-500 hover:bg-green-600 text-white" data-testid="contact-whatsapp-btn">
                        Click here to chat on WhatsApp
                        <MessageCircle className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
